import {
    signIn,
    signOut,
    signUp,
    confirmSignUp,
    resendSignUpCode,
    resetPassword,
    confirmResetPassword,
    fetchAuthSession,
} from "aws-amplify/auth";

// Auth helpers backed by aws-amplify v6 (was amazon-cognito-identity-js, removed to
// clear CVE-2026-46625 — the only fix, js-cookie 3.x, was incompatible with that SDK's
// wildcard `Cookies.set` import). The public method names + the redux session shape are
// preserved so callers (SignInForm, SignUp, ForgotPassword, Logout, Base, userSession) are
// unchanged. Amplify holds the refresh token internally, so `credentials.refreshToken` is
// dropped from the session — nothing consumes it. Amplify.configure() runs in src/index.js.

// Map an Amplify error onto the legacy contract: `.message` for display and `.code` for
// the `e.code === "UserNotConfirmedException"` branch in SignInForm (Amplify uses `.name`).
const normalizeError = (err) => {
    const message =
        (err && (err.message || err.toString())) || "Authentication error";
    const name = err && err.name;
    return Object.assign(new Error(message), { code: name, name });
};

// Build the redux session object from a fetched Amplify token bundle.
const buildSession = (tokens) => {
    const payload = (tokens.idToken && tokens.idToken.payload) || {};
    return {
        credentials: {
            accessToken: tokens.accessToken && tokens.accessToken.toString(),
            idToken: tokens.idToken && tokens.idToken.toString(),
        },
        user: {
            userName: payload["cognito:username"],
            email: payload.email,
        },
    };
};

// Fetch the current session's tokens (optionally forcing a refresh) and shape them like
// the legacy `{ credentials, user }` session. Throws if there is no active session.
const fetchSession = async ({ forceRefresh = false } = {}) => {
    const { tokens } = await fetchAuthSession(
        forceRefresh ? { forceRefresh: true } : undefined
    );
    if (!tokens || !tokens.idToken) {
        throw new Error("No active Cognito session");
    }
    return buildSession(tokens);
};

const ensureSignedOut = async () => {
    try {
        await signOut();
    } catch (_) {
        // already signed out / nothing to do
    }
};

// Sign in and return the redux session. Recovers from a stale Amplify session
// ("already authenticated") by signing out and retrying once.
const authenticateUser = async (username, password) => {
    try {
        let result;
        try {
            result = await signIn({ username, password });
        } catch (e) {
            if (e && e.name === "UserAlreadyAuthenticatedException") {
                await ensureSignedOut();
                result = await signIn({ username, password });
            } else {
                throw e;
            }
        }
        if (!result.isSignedIn) {
            // MFA / new-password-required etc. — surface as an error with a code so the
            // caller can react, mirroring the old onFailure path.
            const step = result.nextStep && result.nextStep.signInStep;
            const err = new Error("Additional sign-in step required: " + step);
            err.name = step || "SignInIncomplete";
            err.code = err.name;
            throw err;
        }
        return await fetchSession();
    } catch (e) {
        throw normalizeError(e);
    }
};

// Register a new user. Signature kept: (email, password, phoneNumber, firstName,
// lastName). phoneNumber was already unused by the old implementation.
const signUpNewUser = async (
    email,
    password,
    phoneNumber,
    firstName,
    lastName
) => {
    try {
        return await signUp({
            username: email,
            password,
            options: {
                userAttributes: {
                    name: `${firstName} ${lastName}`,
                    given_name: firstName,
                    family_name: lastName,
                },
            },
        });
    } catch (e) {
        throw normalizeError(e);
    }
};

const forgotPassword = async (username) => {
    try {
        return await resetPassword({ username });
    } catch (e) {
        throw normalizeError(e);
    }
};

// Complete a forgot-password flow with the emailed code and the new password.
const confirmPassword = async (username, verificationCode, newPassword) => {
    try {
        return await confirmResetPassword({
            username,
            confirmationCode: verificationCode,
            newPassword,
        });
    } catch (e) {
        throw normalizeError(e);
    }
};

const confirmRegistration = async (username, verificationCode) => {
    try {
        return await confirmSignUp({
            username,
            confirmationCode: verificationCode,
        });
    } catch (e) {
        throw normalizeError(e);
    }
};

const resendConfirmationCode = async (username) => {
    try {
        return await resendSignUpCode({ username });
    } catch (e) {
        throw normalizeError(e);
    }
};

const signOutCognitoSession = () => {
    ensureSignedOut().finally(() => {
        window.location = "/login";
    });
};

const utils = {
    fetchSession,
    authenticateUser,
    signUpNewUser,
    forgotPassword,
    confirmPassword,
    confirmRegistration,
    resendConfirmationCode,
    signOutCognitoSession,
};

export default utils;
